import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Check, RefreshCw, Activity } from 'lucide-react';
import Header from '../components/Header';
import apiService from '../services/apiService';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  color: #fff;
  padding: 80px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 600px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 30px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0;
`;

const Section = styled.div`
  background: #141414;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  border: 1px solid #333;
`;

const SectionTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 16px;
  color: #e50914;
  font-size: 1.1rem;
`;

const OptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #222;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-size: 1rem;
  color: #eee;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #333;
  transition: .4s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
`;

const Input = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + ${Slider} {
    background-color: #e50914;
  }

  &:checked + ${Slider}:before {
    transform: translateX(22px);
  }
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: #e50914;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  &:hover {
    background: #f40612;
  }
  
  &:disabled {
      background: #555;
      cursor: not-allowed;
  }
`;

const AdminBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: transparent;
  color: #ccc;
  border: 1px solid #444;
  border-radius: 12px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    border-color: #666;
  }
  
  &:disabled {
      opacity: 0.5;
      cursor: wait;
  }
`;

const TOPICS = [
  { id: 'Economy', label: 'Economy & Business' },
  { id: 'Tech', label: 'Technology' },
  { id: 'Life', label: 'Life & Health' },
  { id: 'Culture', label: 'Culture & Showbiz' },
];

const FeedSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    Economy: true,
    Tech: true,
    Life: true,
    Culture: true
  });
  const [isSaved, setIsSaved] = useState(false);

  // Admin Batch States
  const [batchLoading, setBatchLoading] = useState(null); // 'news' | 'stock' | null
  const [batchResult, setBatchResult] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('feed_settings');
    if (saved) {
      try {
        setSettings({ ...settings, ...JSON.parse(saved) });
      } catch (e) {
        console.error("Failed to parse settings", e);
      }
    }
  }, []);

  const handleToggle = (id) => {
    setSettings(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    setIsSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('feed_settings', JSON.stringify(settings));
    setIsSaved(true);
    setTimeout(() => {
      navigate('/feed'); // Return to feed to apply changes
    }, 500);
  };

  const triggerBatch = async (type) => {
    if (!window.confirm(`Are you sure you want to run the ${type.toUpperCase()} batch manually? This might take a while.`)) return;

    setBatchLoading(type);
    setBatchResult('');

    try {
      if (type === 'news') {
        await apiService.runNewsBatch();
      } else {
        await apiService.runStockBatch();
      }
      setBatchResult(`✅ ${type.toUpperCase()} Batch completed successfully.`);
    } catch (error) {
      console.error(`Batch Error (${type}):`, error);
      setBatchResult(`❌ Error running ${type} batch.`);
    } finally {
      setBatchLoading(null);
    }
  };

  return (
    <>
      <Header />
      <Container>
        <ContentWrapper>
          <PageHeader>
            <BackBtn onClick={() => navigate('/feed')}>
              <ArrowLeft size={24} />
            </BackBtn>
            <Title>Feed Settings</Title>
          </PageHeader>

          <Section>
            <SectionTitle>Select Topics</SectionTitle>
            {TOPICS.map(topic => (
              <OptionRow key={topic.id}>
                <Label>{topic.label}</Label>
                <ToggleSwitch>
                  <Input
                    type="checkbox"
                    checked={settings[topic.id]}
                    onChange={() => handleToggle(topic.id)}
                  />
                  <Slider />
                </ToggleSwitch>
              </OptionRow>
            ))}
          </Section>

          <SaveBtn onClick={handleSave}>
            {isSaved ? <><Check size={20} /> Saved!</> : <><Save size={20} /> Save Changes</>}
          </SaveBtn>

          <p style={{ color: '#666', textAlign: 'center', marginTop: 20, fontSize: '0.9rem' }}>
            * Changes will be applied when you return to the Feed.
          </p>

          {/* Admin Section: Manual Batch Triggers */}
          <div style={{ marginTop: '40px' }}>
            <Section>
              <SectionTitle style={{ color: '#888' }}>Admin Actions: On-Demand Batch</SectionTitle>
              <p style={{ color: '#777', fontSize: '0.9rem', marginBottom: '16px', lineHeight: 1.5 }}>
                Trigger scheduled data scraping tasks manually. The automated scheduler (cron) is now disabled.
              </p>

              <AdminBtn
                onClick={() => triggerBatch('news')}
                disabled={batchLoading !== null}
              >
                {batchLoading === 'news' ? <RefreshCw size={20} className="spin" /> : <RefreshCw size={20} />}
                {batchLoading === 'news' ? 'Running News Batch...' : 'Run News Batch Manually'}
              </AdminBtn>

              <AdminBtn
                onClick={() => triggerBatch('stock')}
                disabled={batchLoading !== null}
                style={{ marginTop: '12px' }}
              >
                {batchLoading === 'stock' ? <Activity size={20} className="spin" /> : <Activity size={20} />}
                {batchLoading === 'stock' ? 'Running Stock Batch...' : 'Run Stock Batch Manually'}
              </AdminBtn>

              {batchResult && (
                <p style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: batchResult.includes('✅') ? 'rgba(0, 230, 118, 0.1)' : 'rgba(255, 82, 82, 0.1)',
                  color: batchResult.includes('✅') ? '#00e676' : '#ff5252',
                  fontSize: '0.9rem',
                  textAlign: 'center'
                }}>
                  {batchResult}
                </p>
              )}
            </Section>
          </div>

        </ContentWrapper>
      </Container>
    </>
  );
};

export default FeedSettingsPage;
